import { useState, type FormEvent } from "react";
import AppLayout from "@/components/feature/AppLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { faqs, testimonials, siteName, siteSlogan } from "@/mocks/narutoContent";

const CONTACT_URL = "https://readdy.ai/api/form/da2jf8rd3r93mn1k3a3g";

const values = [
  { icon: "ri-heart-3-line", title: "用热爱创作", desc: "每篇同人、每幅插画，都源于对这个世界真实的喜爱。" },
  { icon: "ri-user-heart-line", title: "尊重每一位作者", desc: "标注署名、保护版权，让原创者的付出被看见。" },
  { icon: "ri-global-line", title: "开放与共享", desc: "优质内容免费阅读，让更多同好能轻松看到好作品。" },
  { icon: "ri-leaf-line", title: "纯粹不打扰", desc: "专注内容本身，不掺水、不灌水、不套路。" },
];

const contactInfo = [
  { icon: "ri-mail-line", label: "投稿 / 反馈邮箱", value: "yingye.club@example.com" },
  { icon: "ri-time-line", label: "在线时间", value: "每日 9:00 — 23:00" },
  { icon: "ri-message-3-line", label: "投稿须知", value: "请附作品标题、作者署名与简介" },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      setMessage("已收到，感谢你的信任，我们会尽快与你联系！");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "phone_alt") payload.append(key, value as string);
    });

    setStatus("loading");
    try {
      const res = await fetch(CONTACT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam =
        !res.ok || !parsed?.code || /spam/i.test(responseText) || /spam/i.test(serverMsg || "");
      if (isSpam) {
        setStatus("error");
        setMessage(serverMsg && serverMsg !== "form data is spam" ? serverMsg : "提交失败，请稍后再试");
        return;
      }
      setStatus("success");
      setMessage(parsed?.meta?.message || "提交成功！我们会尽快查看并回复你。");
      form.reset();
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后重试");
    }
  }

  useSeo({
    title: "关于影页社 - 火影忍者同人图文站简介 | 影页社",
    description:
      "了解影页社：一个专注火影忍者同人小说、同人图、高清壁纸与短篇速读的粉丝内容站。这里有品牌介绍、发展理念、内容原则、用户口碑与投稿联系方式。",
    keywords: "关于影页社,火影忍者同人站,火影同人投稿,火影同人图文站",
    canonical: "https://wn-baidu.cn/guanyu",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于影页社",
        description: "火影忍者同人图文内容站的品牌介绍与发展理念",
        url: "https://wn-baidu.cn/guanyu",
        mainEntity: {
          "@type": "Organization",
          name: siteName,
          email: "yingye.club@example.com",
          description: "专注火影忍者同人图文与小说的一站式内容平台",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系我们",
        url: "https://wn-baidu.cn/guanyu",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  return (
    <AppLayout>
      <div className="pt-20">
        <Breadcrumb items={[{ name: "关于我们" }]} />

        {/* 品牌介绍 */}
        <section className="mx-auto w-full max-w-7xl px-4 md:px-6 pt-6 pb-12">
          <div className="grid gap-8 lg:grid-cols-2 items-center">
            <div>
              <span className="mb-3 inline-flex items-center gap-2 font-label text-xs tracking-widest text-primary-600">
                <i className="ri-quill-pen-line"></i> 关于 {siteName}
              </span>
              <h1 className="font-display text-3xl md:text-5xl text-foreground-950 leading-tight">
                {siteSlogan}
              </h1>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
                {siteName}是一个由同人爱好者自发搭建与维护的粉丝内容站。我们相信，一部作品真正的生命力，不止在正片里，也在千千万万个同好笔下与笔刷中重新生长出来的故事与画面。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                从同人小说、同人文，到同人插画、高清壁纸，再到五六分钟就能读完的短篇速读，我们希望用一张图、一段文，帮你重拾与木叶有关的那些心动瞬间。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 font-label text-sm whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-line"></i> 投稿 / 联系我们
                </a>
                <a
                  href="#faq"
                  className="inline-flex items-center gap-2 rounded-full border border-primary-300 px-6 py-3 font-label text-sm whitespace-nowrap text-primary-700 transition-colors hover:bg-primary-100"
                >
                  <i className="ri-question-line"></i> 常见问题
                </a>
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=anime%20illustration%20of%20a%20young%20ninja%20sitting%20under%20a%20big%20tree%20reading%20a%20scroll%20while%20autumn%20leaves%20fall%20around%20him%2C%20warm%20golden%20afternoon%20light%2C%20ink%20wash%20japanese%20style%2C%20peaceful%20contemplative%20mood%2C%20high%20detail&width=1000&height=760&seq=naruto-about-1&orientation=portrait"
                alt="影页社品牌插画 · 树下读卷的同人少年"
                title="影页社 · 火影忍者同人创作精神"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* 定位与理念 */}
        <section className="bg-background-100/70">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-16">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
                <h2 className="font-display text-2xl md:text-3xl text-foreground-950">网站定位</h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  面向火影忍者同人爱好者的一站式图文内容平台。我们不做搬运与堆砌，而是人工精选高质量的同人小说、同人文、同人插画与高清壁纸，并将短篇速读作为每日更新栏目，帮助你在碎片时间里快速读到走心的好内容。
                </p>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  无论你是资深老粉、新入坑的观众，还是想展示作品的同人创作者，这里都有一席之地。
                </p>
              </div>
              <div>
                <h2 className="font-display text-2xl md:text-3xl text-foreground-950">发展理念</h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  我们相信同人文化最珍贵的，是那份“因为热爱所以愿意创作”的纯粹。影页社愿意做这样一件事：把散落在各地的优秀同人内容整理起来，让好作品被更多同好看见，也让每一位创作者的努力得到应有的尊重。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 内容原则 */}
        <section className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-16">
          <h2 className="text-center font-display text-2xl md:text-3xl text-foreground-950">我们的内容原则</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 text-center">
                <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary-100 text-primary-700">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-display text-xl text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 口碑 */}
        <section className="bg-background-100/70">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-16">
            <h2 className="text-center font-display text-2xl md:text-3xl text-foreground-950">来自同好的声音</h2>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <div className="flex items-center gap-1 text-primary-500">
                    {[1, 2, 3, 4, 5].map((s) => <i key={s} className="ri-star-fill text-sm"></i>)}
                  </div>
                  <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-primary-100 font-display text-base text-primary-700">{t.name[0]}</span>
                    <div>
                      <p className="font-label text-sm text-foreground-900">{t.name}</p>
                      <p className="font-label text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 投稿与联系 */}
        <section id="contact" className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-16">
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <h2 className="font-display text-2xl md:text-3xl text-foreground-950">投稿 & 联系我们</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                无论是想投稿自己的同人小说、同人图，还是对我们的内容有建议，都欢迎留言。我们会认真查看每一条来信。
              </p>
              <ul className="mt-6 space-y-4">
                {contactInfo.map((c) => (
                  <li key={c.label} className="flex items-start gap-3">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-secondary-100 text-secondary-800">
                      <i className={`${c.icon} text-lg`}></i>
                    </span>
                    <div>
                      <p className="font-label text-xs text-foreground-500">{c.label}</p>
                      <p className="mt-0.5 text-sm text-foreground-900">{c.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <form
              data-readdy-form="da2jf8rd3r93mn1k3a3g"
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7 space-y-4"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block font-label text-sm text-foreground-800">你的称呼</label>
                  <input id="contact-name" name="name" type="text" required maxLength={30} placeholder="怎么称呼你" className="w-full rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block font-label text-sm text-foreground-800">联系邮箱</label>
                  <input id="contact-email" name="email" type="email" required maxLength={80} placeholder="用于接收回复" className="w-full rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30" />
                </div>
              </div>
              <div>
                <label htmlFor="contact-type" className="mb-1.5 block font-label text-sm text-foreground-800">来信类型</label>
                <select id="contact-type" name="type" className="w-full rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30">
                  <option value="投稿同人小说">投稿同人小说 / 短篇</option>
                  <option value="投稿同人图">投稿同人图 / 插画</option>
                  <option value="建议反馈">内容建议 / 反馈</option>
                  <option value="其他">其他事项</option>
                </select>
              </div>
              <div>
                <label htmlFor="contact-msg" className="mb-1.5 block font-label text-sm text-foreground-800">内容描述</label>
                <textarea id="contact-msg" name="message" required maxLength={500} rows={5} placeholder="请描述你的投稿内容或建议（限 500 字）" className="w-full resize-none rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30"></textarea>
              </div>
              <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="website_alt" />
              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 font-label text-sm whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                {status === "loading" ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
                提交来信
              </button>
              {status !== "idle" && (
                <p
                  role={status === "error" ? "alert" : "status"}
                  className={`rounded-md px-4 py-2.5 text-sm ${status === "success" ? "bg-primary-100 text-primary-800" : "bg-accent-100 text-accent-800"}`}
                >
                  {message}
                </p>
              )}
            </form>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-background-100/70">
          <div className="mx-auto w-full max-w-4xl px-4 md:px-6 py-14 md:py-16">
            <h2 className="text-center font-display text-2xl md:text-3xl text-foreground-950">常见问题</h2>
            <div className="mt-8 space-y-3">
              {faqs.map((f) => (
                <div key={f.q} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="font-label text-sm font-medium text-foreground-900 md:text-base">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </AppLayout>
  );
}